import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../../../components/layout/AdminLayout';
import { supabase } from '../../../lib/supabase';
import { toast } from 'sonner';
import { 
  Shield, 
  Search, 
  ChevronDown, 
  ChevronRight, 
  Save, 
  X, 
  User,
  Loader2,
  ShieldCheck,
  ShieldX,
  RefreshCw,
  Globe,
  Plus,
  Trash2
} from 'lucide-react';
import { 
  ADMIN_MODULES, 
  ADMIN_SUBROUTES, 
  updateUserAdminPermissions, 
  getUserAdminRestrictions 
} from '../../../hooks/useAdminPermissions';

interface AdminUser {
  id: string;
  email: string;
  name: string | null;
  role: string;
  is_active: boolean;
  permissions: any;
  created_at: string;
}

export default function AdminPermissions() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [expandedModules, setExpandedModules] = useState<string[]>([]);
  const [restrictedRoutes, setRestrictedRoutes] = useState<string[]>([]);
  const [allowedDomains, setAllowedDomains] = useState<string[]>([]);
  const [newDomain, setNewDomain] = useState('');
  const [availableDomains, setAvailableDomains] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [activeTab, setActiveTab] = useState<'routes' | 'domains'>('routes');

  // Charger les utilisateurs admin
  const loadUsers = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('users')
        .select('id, email, name, role, is_active, permissions, created_at')
        .in('role', ['admin', 'super_admin'])
        .order('name', { ascending: true });

      if (error) throw error;
      setUsers(data || []);
    } catch (err: any) {
      console.error('Erreur chargement utilisateurs:', err);
      toast.error('Erreur lors du chargement des utilisateurs');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  // Charger les domaines disponibles
  const loadAvailableDomains = async () => {
    try {
      const { data, error } = await supabase
        .from('tenants')
        .select('domain_name')
        .not('domain_name', 'is', null);
      
      if (!error && data) {
        const domains = data.map(t => t.domain_name).filter(Boolean);
        setAvailableDomains(domains);
      }
    } catch (err) {
      console.error('Erreur chargement domaines:', err);
    }
  };

  useEffect(() => {
    loadAvailableDomains();
  }, []);

  // Charger les restrictions d'un utilisateur
  const loadUserRestrictions = async (user: AdminUser) => {
    setSelectedUser(user);
    const { restrictedRoutes: routes, allowedDomains: domains } = await getUserAdminRestrictions(user.id);
    setRestrictedRoutes(routes);
    setAllowedDomains(domains);
    setHasChanges(false);
    setActiveTab('routes');
  };

  // Filtrer les utilisateurs
  const filteredUsers = users.filter(user => 
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (user.name && user.name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Toggle expansion d'un module
  const toggleModule = (moduleId: string) => {
    setExpandedModules(prev => 
      prev.includes(moduleId) 
        ? prev.filter(id => id !== moduleId)
        : [...prev, moduleId]
    );
  };

  // Vérifier si un module est restreint (route complète avec /*)
  const isModuleRestricted = (route: string): boolean => {
    return restrictedRoutes.includes(route + '/*') || restrictedRoutes.includes(route);
  };

  // Vérifier si une sous-route est restreinte
  const isSubrouteRestricted = (route: string): boolean => {
    // Vérifier restriction exacte
    if (restrictedRoutes.includes(route)) return true;
    
    // Vérifier si le module parent est restreint avec /*
    const parentModule = ADMIN_MODULES.find(m => route.startsWith(m.route));
    if (parentModule && restrictedRoutes.includes(parentModule.route + '/*')) {
      return true;
    }
    
    return false;
  };

  // Toggle restriction d'un module complet
  const toggleModuleRestriction = (module: typeof ADMIN_MODULES[0]) => {
    const routePattern = module.hasSubroutes ? module.route + '/*' : module.route;
    
    setRestrictedRoutes(prev => {
      let newRoutes: string[];
      
      if (prev.includes(routePattern)) {
        // Enlever la restriction du module
        newRoutes = prev.filter(r => r !== routePattern);
      } else {
        // Ajouter la restriction du module et enlever les sous-routes individuelles
        newRoutes = prev.filter(r => !r.startsWith(module.route));
        newRoutes.push(routePattern);
      }
      
      return newRoutes;
    });
    setHasChanges(true);
  };

  // Toggle restriction d'une sous-route
  const toggleSubrouteRestriction = (subroute: string, moduleRoute: string) => {
    setRestrictedRoutes(prev => {
      let newRoutes = [...prev];
      
      // Si le module parent est restreint avec /*, on doit d'abord le décomposer
      const parentPattern = moduleRoute + '/*';
      if (newRoutes.includes(parentPattern)) {
        // Enlever le pattern parent
        newRoutes = newRoutes.filter(r => r !== parentPattern);
        
        // Ajouter toutes les sous-routes SAUF celle qu'on veut autoriser
        const subroutes = ADMIN_SUBROUTES[ADMIN_MODULES.find(m => m.route === moduleRoute)?.id || ''] || [];
        subroutes.forEach(sr => {
          if (sr.route !== subroute) {
            newRoutes.push(sr.route);
          }
        });
      } else {
        // Toggle simple de la sous-route
        if (newRoutes.includes(subroute)) {
          newRoutes = newRoutes.filter(r => r !== subroute);
        } else {
          newRoutes.push(subroute);
        }
      }
      
      return newRoutes;
    });
    setHasChanges(true);
  };

  // Ajouter un domaine autorisé
  const addAllowedDomain = (domain: string) => {
    if (domain && !allowedDomains.includes(domain)) {
      setAllowedDomains(prev => [...prev, domain]);
      setNewDomain('');
      setHasChanges(true);
    }
  };

  // Supprimer un domaine autorisé
  const removeAllowedDomain = (domain: string) => {
    setAllowedDomains(prev => prev.filter(d => d !== domain));
    setHasChanges(true);
  };

  // Sauvegarder les restrictions
  const saveRestrictions = async () => {
    if (!selectedUser) return;
    
    setIsSaving(true);
    try {
      const { success, error } = await updateUserAdminPermissions(selectedUser.id, restrictedRoutes, allowedDomains);
      
      if (!success) {
        throw new Error(error);
      }
      
      toast.success(`Permissions mises à jour pour ${selectedUser.name || selectedUser.email}`);
      setHasChanges(false);
      
      // Recharger les utilisateurs pour mettre à jour l'affichage
      await loadUsers();
    } catch (err: any) {
      console.error('Erreur sauvegarde:', err);
      toast.error('Erreur lors de la sauvegarde des permissions');
    } finally {
      setIsSaving(false);
    }
  };

  // Compter les restrictions d'un utilisateur
  const countRestrictions = (user: AdminUser): number => {
    const routeCount = user.permissions?.admin_access?.restricted_routes?.length || 0;
    const domainCount = user.permissions?.admin_access?.allowed_domains?.length || 0;
    return routeCount + domainCount;
  };

  return (
    <AdminLayout>
      <div className="p-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-gradient-to-br from-[#D4AF37]/20 to-[#D4AF37]/5 rounded-lg">
              <Shield className="w-6 h-6 text-[#D4AF37]" />
            </div>
            <h1 className="text-2xl font-bold text-white">Permissions Admin</h1>
          </div>
          <p className="text-gray-400">
            Gérez les restrictions d'accès aux pages admin pour chaque compte administrateur.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Liste des utilisateurs */}
          <div className="lg:col-span-1">
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-white">Administrateurs</h2>
                <button
                  onClick={loadUsers}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  title="Rafraîchir"
                >
                  <RefreshCw className="w-4 h-4 text-gray-400" />
                </button>
              </div>
              
              {/* Recherche */}
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#D4AF37]/50"
                />
              </div>

              {/* Liste */}
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 text-[#D4AF37] animate-spin" />
                </div>
              ) : (
                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  {filteredUsers.map(user => (
                    <button
                      key={user.id}
                      onClick={() => loadUserRestrictions(user)}
                      className={`w-full text-left p-3 rounded-lg transition-colors ${
                        selectedUser?.id === user.id
                          ? 'bg-[#D4AF37]/20 border border-[#D4AF37]/50'
                          : 'bg-white/5 hover:bg-white/10 border border-transparent'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-full ${
                          user.role === 'super_admin' 
                            ? 'bg-purple-500/20' 
                            : 'bg-blue-500/20'
                        }`}>
                          <User className={`w-4 h-4 ${
                            user.role === 'super_admin' 
                              ? 'text-purple-400' 
                              : 'text-blue-400'
                          }`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-medium truncate">
                            {user.name || 'Sans nom'}
                          </p>
                          <p className="text-gray-400 text-sm truncate">
                            {user.email}
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          {user.role === 'super_admin' ? (
                            <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded-full">
                              Super Admin
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded-full">
                              Admin
                            </span>
                          )}
                          {countRestrictions(user) > 0 && (
                            <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded-full">
                              {countRestrictions(user)} restriction(s)
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                  
                  {filteredUsers.length === 0 && (
                    <p className="text-center text-gray-500 py-4">
                      Aucun administrateur trouvé
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Panneau de configuration */}
          <div className="lg:col-span-2">
            {selectedUser ? (
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
                {/* Header utilisateur */}
                <div className="flex items-center justify-between mb-6 pb-4 border-b border-white/10">
                  <div className="flex items-center gap-4">
                    <div className={`p-3 rounded-full ${
                      selectedUser.role === 'super_admin' 
                        ? 'bg-purple-500/20' 
                        : 'bg-blue-500/20'
                    }`}>
                      <User className={`w-6 h-6 ${
                        selectedUser.role === 'super_admin' 
                          ? 'text-purple-400' 
                          : 'text-blue-400'
                      }`} />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-white">
                        {selectedUser.name || selectedUser.email}
                      </h2>
                      <p className="text-gray-400">{selectedUser.email}</p>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => {
                      setSelectedUser(null);
                      setRestrictedRoutes([]);
                      setAllowedDomains([]);
                      setHasChanges(false);
                    }}
                    className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-400" />
                  </button>
                </div>

                {/* Message Super Admin */}
                {selectedUser.role === 'super_admin' ? (
                  <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4 mb-6">
                    <div className="flex items-center gap-3">
                      <ShieldCheck className="w-5 h-5 text-purple-400" />
                      <p className="text-purple-300">
                        Les <strong>Super Admins</strong> ont accès à toutes les pages et tous les domaines sans restriction.
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Onglets */}
                    <div className="flex gap-2 mb-6">
                      <button
                        onClick={() => setActiveTab('routes')}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                          activeTab === 'routes'
                            ? 'bg-[#D4AF37]/20 text-[#D4AF37] border border-[#D4AF37]/50'
                            : 'bg-white/5 text-gray-400 hover:bg-white/10'
                        }`}
                      >
                        <ShieldX className="w-4 h-4" />
                        Restrictions de pages
                        {restrictedRoutes.length > 0 && (
                          <span className="px-1.5 py-0.5 bg-red-500/20 text-red-400 text-xs rounded-full">
                            {restrictedRoutes.length}
                          </span>
                        )}
                      </button>
                      <button
                        onClick={() => setActiveTab('domains')}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                          activeTab === 'domains'
                            ? 'bg-[#D4AF37]/20 text-[#D4AF37] border border-[#D4AF37]/50'
                            : 'bg-white/5 text-gray-400 hover:bg-white/10'
                        }`}
                      >
                        <Globe className="w-4 h-4" />
                        Domaines autorisés
                        {allowedDomains.length > 0 && (
                          <span className="px-1.5 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full">
                            {allowedDomains.length}
                          </span>
                        )}
                      </button>
                    </div>

                    {activeTab === 'routes' && (
                      <>
                        {/* Instructions Routes */}
                        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-6">
                          <p className="text-blue-300 text-sm">
                            <strong>Par défaut</strong>, cet administrateur a accès à toutes les pages.
                            Cochez les modules ou sous-pages que vous souhaitez <strong>restreindre</strong>.
                          </p>
                        </div>

                        {/* Liste des modules */}
                    <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
                      {ADMIN_MODULES.map(module => {
                        const moduleId = module.id;
                        const isExpanded = expandedModules.includes(moduleId);
                        const hasSubroutes = module.hasSubroutes && ADMIN_SUBROUTES[moduleId];
                        const isRestricted = isModuleRestricted(module.route);
                        
                        return (
                          <div key={moduleId} className="border border-white/10 rounded-lg overflow-hidden">
                            {/* Module header */}
                            <div 
                              className={`flex items-center gap-3 p-3 ${
                                isRestricted ? 'bg-red-500/10' : 'bg-white/5'
                              }`}
                            >
                              {hasSubroutes && (
                                <button
                                  onClick={() => toggleModule(moduleId)}
                                  className="p-1 hover:bg-white/10 rounded"
                                >
                                  {isExpanded ? (
                                    <ChevronDown className="w-4 h-4 text-gray-400" />
                                  ) : (
                                    <ChevronRight className="w-4 h-4 text-gray-400" />
                                  )}
                                </button>
                              )}
                              
                              <label className="flex items-center gap-3 flex-1 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={isRestricted}
                                  onChange={() => toggleModuleRestriction(module)}
                                  className="w-4 h-4 rounded border-gray-600 text-red-500 focus:ring-red-500 focus:ring-offset-0 bg-white/10"
                                />
                                <span className={`font-medium ${isRestricted ? 'text-red-400' : 'text-white'}`}>
                                  {module.name}
                                </span>
                                <span className="text-gray-500 text-sm">
                                  {module.route}
                                </span>
                              </label>
                              
                              {isRestricted && (
                                <ShieldX className="w-4 h-4 text-red-400" />
                              )}
                            </div>

                            {/* Sous-routes */}
                            {hasSubroutes && isExpanded && (
                              <div className="border-t border-white/10 bg-black/20">
                                {ADMIN_SUBROUTES[moduleId].map(subroute => {
                                  const isSubRestricted = isSubrouteRestricted(subroute.route);
                                  const isParentRestricted = restrictedRoutes.includes(module.route + '/*');
                                  
                                  return (
                                    <label
                                      key={subroute.route}
                                      className={`flex items-center gap-3 p-3 pl-12 cursor-pointer hover:bg-white/5 ${
                                        isSubRestricted ? 'bg-red-500/5' : ''
                                      }`}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={isSubRestricted}
                                        onChange={() => toggleSubrouteRestriction(subroute.route, module.route)}
                                        disabled={isParentRestricted}
                                        className="w-4 h-4 rounded border-gray-600 text-red-500 focus:ring-red-500 focus:ring-offset-0 bg-white/10 disabled:opacity-50"
                                      />
                                      <span className={`text-sm ${isSubRestricted ? 'text-red-400' : 'text-gray-300'}`}>
                                        {subroute.name}
                                      </span>
                                      <span className="text-gray-600 text-xs">
                                        {subroute.route}
                                      </span>
                                    </label>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                        </div>
                      </>
                    )}

                    {activeTab === 'domains' && (
                      <>
                        {/* Instructions Domaines */}
                        <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 mb-6">
                          <p className="text-green-300 text-sm">
                            <strong>Par défaut</strong>, cet administrateur a accès à tous les domaines.
                            Ajoutez des domaines pour <strong>limiter l'accès uniquement à ceux-ci</strong>.
                            Si la liste est vide, l'accès est autorisé sur tous les domaines.
                          </p>
                        </div>

                        {/* Liste des domaines autorisés */}
                        <div className="space-y-4">
                          {/* Domaines actuellement autorisés */}
                          {allowedDomains.length > 0 ? (
                            <div className="space-y-2">
                              <p className="text-sm text-gray-400 mb-2">Domaines autorisés :</p>
                              {allowedDomains.map(domain => (
                                <div 
                                  key={domain}
                                  className="flex items-center justify-between p-3 bg-green-500/10 border border-green-500/30 rounded-lg"
                                >
                                  <div className="flex items-center gap-3">
                                    <Globe className="w-4 h-4 text-green-400" />
                                    <span className="text-white">{domain}</span>
                                  </div>
                                  <button
                                    onClick={() => removeAllowedDomain(domain)}
                                    className="p-1 hover:bg-red-500/20 rounded transition-colors"
                                    title="Supprimer"
                                  >
                                    <Trash2 className="w-4 h-4 text-red-400" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="p-4 bg-white/5 border border-white/10 rounded-lg text-center">
                              <Globe className="w-8 h-8 text-gray-500 mx-auto mb-2" />
                              <p className="text-gray-400 text-sm">
                                Aucune restriction de domaine. Accès à tous les domaines.
                              </p>
                            </div>
                          )}

                          {/* Ajouter un domaine */}
                          <div className="mt-4">
                            <p className="text-sm text-gray-400 mb-2">Ajouter un domaine :</p>
                            
                            {/* Recherche/Saisie manuelle */}
                            <div className="flex gap-2 mb-3">
                              <div className="relative flex-1">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                  type="text"
                                  placeholder="Rechercher ou saisir un domaine..."
                                  value={newDomain}
                                  onChange={(e) => setNewDomain(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' && newDomain) {
                                      addAllowedDomain(newDomain);
                                    }
                                  }}
                                  className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#D4AF37]/50"
                                />
                              </div>
                              <button
                                onClick={() => addAllowedDomain(newDomain)}
                                disabled={!newDomain}
                                className="flex items-center gap-2 px-4 py-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <Plus className="w-4 h-4" />
                                Ajouter
                              </button>
                            </div>

                            {/* Liste des domaines disponibles filtrée */}
                            <div className="max-h-[200px] overflow-y-auto border border-white/10 rounded-lg">
                              {availableDomains
                                .filter(d => !allowedDomains.includes(d))
                                .filter(d => !newDomain || d.toLowerCase().includes(newDomain.toLowerCase()))
                                .length === 0 ? (
                                  <p className="p-3 text-gray-500 text-sm text-center">
                                    {newDomain ? 'Aucun domaine correspondant' : 'Aucun domaine disponible'}
                                  </p>
                                ) : (
                                  availableDomains
                                    .filter(d => !allowedDomains.includes(d))
                                    .filter(d => !newDomain || d.toLowerCase().includes(newDomain.toLowerCase()))
                                    .map(domain => (
                                      <button
                                        key={domain}
                                        onClick={() => addAllowedDomain(domain)}
                                        className="w-full flex items-center gap-3 p-3 hover:bg-white/10 transition-colors text-left border-b border-white/5 last:border-b-0"
                                      >
                                        <Globe className="w-4 h-4 text-gray-400" />
                                        <span className="text-white">{domain}</span>
                                        <Plus className="w-4 h-4 text-green-400 ml-auto" />
                                      </button>
                                    ))
                                )
                              }
                            </div>
                          </div>
                        </div>
                      </>
                    )}

                    {/* Bouton sauvegarder */}
                    {hasChanges && (
                      <div className="mt-6 pt-4 border-t border-white/10 flex justify-end">
                        <button
                          onClick={saveRestrictions}
                          disabled={isSaving}
                          className="flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-[#D4AF37] to-[#B8941F] text-[#001122] font-medium rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
                        >
                          {isSaving ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Save className="w-4 h-4" />
                          )}
                          Sauvegarder les permissions
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            ) : (
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-12 flex flex-col items-center justify-center text-center">
                <div className="p-4 bg-white/5 rounded-full mb-4">
                  <Shield className="w-12 h-12 text-gray-500" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">
                  Sélectionnez un administrateur
                </h3>
                <p className="text-gray-400 max-w-md">
                  Cliquez sur un administrateur dans la liste pour configurer ses restrictions d'accès aux pages admin.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
